import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { UnControlled as CodeMirror } from "react-codemirror2";
import "codemirror/addon/display/autorefresh";
import "./codemirror_modes/codemirror_yara";
import { RenderWhen } from "../../../components";
import YaraEditorWarningControls from "./YaraEditorWarningControls";

const IDENT_SIZE = 4;
const addGutter = (editor, line, msg, type) => {
  if (!editor || line < 1) return;
  const gutter = document.createElement("div");
  const icon = gutter.appendChild(document.createElement("span"));
  icon.className = `icon-${type}`;
  icon.title = msg;
  icon.setAttribute("data-marker-type", type);
  editor.setGutterMarker(line - 1, "CodeMirror-lint-markers", icon);
  editor.markText(
    { line: line - 1, ch: 0 },
    { line: line - 1, ch: 1 },
    { attributes: { "data-market-type": type } }
  );
};
const showErrorOnLine = (editor, line, msg) => {
  if (!editor || line < 1) return null;
  const errWidget = document.createElement("div");
  errWidget.appendChild(document.createTextNode(msg));
  errWidget.className = "yara-widget-error";
  const top = editor.charCoords({ line, ch: 0 }, "local").top;
  const middleHeight = editor.getScrollerElement().offsetHeight / 2;
  editor.scrollTo(null, top - middleHeight - 5);
  return editor.addLineWidget(line - 1, errWidget, {
    coverGutter: false,
    noHScroll: true
  });
};
const clearAllGutters = editor => {
  editor.clearGutter("CodeMirror-lint-markers");
};
const clearWidget = widget => {
  if (!widget) return;
  widget.clear();
};

const YaraEditor = React.forwardRef((props, editorRef) => {
  const {
    ruleId,
    content,
    onChange,
    error = null,
    warnings = [],
    className,
    options,
    readOnly
  } = props;
  const errWidgetRef = useRef();
  useEffect(() => {
    // Clear editor from errors, than add new errors if they are present
    clearAllGutters(editorRef.current);
    clearWidget(errWidgetRef.current);

    if (!error) return;
    addGutter(editorRef.current, error.line, error.msg, "error");
    errWidgetRef.current = showErrorOnLine(
      editorRef.current,
      error.line,
      error.msg
    );
  }, [editorRef, error?.msg, error?.line]);

  useEffect(() => {
    clearAllGutters(editorRef.current);
    warnings.forEach(warning => {
      addGutter(editorRef.current, warning.line, warning.msg, "warning");
    });
  }, [editorRef, warnings]);

  return (
    <>
      <RenderWhen condition={!!warnings.length}>
        <YaraEditorWarningControls
          warnings={warnings}
          editor={editorRef.current}
        />
      </RenderWhen>
      <div
        className={`yara-editor-codemirror ${className}`}
        data-testid="yara_editor_input_for_content"
      >
        <CodeMirror
          value={content}
          options={{
            mode: "text/x-yara",
            lineWrapping: true,
            indentUnit: IDENT_SIZE,
            tabSize: IDENT_SIZE,
            indentWithTabs: true,
            lineNumbers: true,
            autoRefresh: true,
            gutters: ["CodeMirror-lint-markers"],
            readOnly,
            ...options
          }}
          editorDidMount={cmEditor => (editorRef.current = cmEditor)}
          onChange={(cmEditor, { origin }) => {
            // Prevent onChange when fetching a new ruleset from history
            if (origin) {
              onChange(cmEditor.doc.getValue());
            }
          }}
        />
      </div>
    </>
  );
});

YaraEditor.propTypes = {
  ruleId: PropTypes.number,
  error: PropTypes.shape({
    line: PropTypes.number,
    msg: PropTypes.string
  }),
  warnings: PropTypes.arrayOf(
    PropTypes.shape({
      line: PropTypes.number,
      msg: PropTypes.string
    })
  ),
  onChange: PropTypes.func,
  content: PropTypes.string,
  className: PropTypes.string,
  options: PropTypes.shape({}),
  readOnly: PropTypes.bool
};

export default YaraEditor;
